import * as React from 'react';
import {useSelector} from 'react-redux';

import {Assignables} from 'nutshell-core/assignables';

import {AssigneeSelect} from 'shells/assignee-select/assignee-select';
import {Label} from 'shells/form/label';
import {Stack} from 'shells/layout/stack/stack';

import type {
    GetClosedLeadFollowupAutomation_closedLeadFollowupAutomation_tags as Tag,
    GetClosedLeadFollowupAutomation_closedLeadFollowupAutomation_assigneeOrRule as FollowupLeadAutomationAssigneeOrRule,
} from 'nutshell-graphql-types';

import {GraphQLSelectTags} from '../../select/graphql-select-tags';
import type {Option} from '../../select/graphql-select-tags';

import {
    AUTO_ASSIGN_OPTION,
    AUTO_RESOLVE_ID,
    SAME_ASSIGNEE_ID,
    SAME_ASSIGNEE_OPTION,
} from './consts';
import {PipelineSelectPicker} from './pipeline-select-picker';

type Props = {
    pipelineId: string | null | undefined;
    assigneeId: string | null | undefined;
    tags: Option[];
    onPipelineChange: (pipelineId: string | null | undefined) => void;
    onAssigneeChange: (assigneeId: string | null | undefined) => void;
    onTagsChange: (tags: Option[]) => void;
};

export function CreateLeadFormFields(props: Props) {
    const assignables = useSelector((state) => Assignables.getAll(state));

    return (
        <Stack spacing={32}>
            <div>
                <Label required={true} htmlFor='PipelinePicker'>
                    Which pipeline should the new lead be created in?
                </Label>
                <PipelineSelectPicker
                    inputId='PipelinePicker'
                    selectedPipeline={props.pipelineId}
                    onSelectPipeline={props.onPipelineChange}
                    useAutoDistribute={false}
                />
            </div>
            <div>
                <Label required={true} htmlFor='AssigneePicker'>
                    Who should it be assigned to?
                </Label>
                <AssigneeSelect
                    inputId='AssigneePicker'
                    onChange={props.onAssigneeChange}
                    value={props.assigneeId}
                    isClearable={false}
                    options={[AUTO_ASSIGN_OPTION, SAME_ASSIGNEE_OPTION, ...assignables]}
                />
            </div>
            <div>
                <Label htmlFor='TagsPicker'>Add additional tags</Label>
                <GraphQLSelectTags
                    inputId='TagsPicker'
                    tagType='LEAD'
                    selectedValues={props.tags}
                    onChange={props.onTagsChange}
                />
            </div>
        </Stack>
    );
}

export function convertTagsToOptions(tags: Tag[]): Option[] {
    return tags.map((tag) => ({
        id: tag.id,
        name: tag.name,
        colorName: 'offWhiteDk',
    }));
}

export function getAssigneeId(assigneeOrRule: FollowupLeadAutomationAssigneeOrRule) {
    if (assigneeOrRule.__typename === 'LeadAssigneeRule') {
        if (assigneeOrRule.assignmentRule === 'AUTO_ASSIGN') {
            return AUTO_RESOLVE_ID;
        }

        return SAME_ASSIGNEE_ID;
    }

    return assigneeOrRule.id;
}
